import { useCallback } from 'react'
import { useRecoilValue, useSetRecoilState, useRecoilCallback } from 'recoil'
import { NON_EDITABLE_IMAGE_SUBTYPES } from '@messenger/consts'
import { caseAtom } from '@/atoms/caseAtoms'
import { unsavedChangesAtom, itemSelector } from '@/atoms/_editorAtoms'
import {
  editorPagesAtom,
  isBulkProcessingAtom,
  bulkProcessingProgressAtom,
} from '@/atoms/_editorAtoms'
import {
  convertGammaPercentageToInternalValue,
  convertSaturationPercentageToOffset,
  removeProgrammaticColorAdjustmentsFromImageEdits,
  useProgrammaticImageEdit,
} from '@/hooks/editor/useProgrammaticImageEdit'
import { determineImageUrl } from '@/utils/editor/determineImageUrl'
import { isImagePlaceholder } from '@/utils/editor/imagePlaceholder'
import type { Content, ImageItem } from '@messenger/types'

type BulkQuickActionType = 'lightening' | 'redness' | 'reset'

/**
 * The AI provenance a processed image keeps: its accepted cutout when it was
 * rendered from one, else nothing, which clears a stale tag.
 */
type ProcessedCutout = {
  aiVariantId: string | undefined
  aiVariantUrl: string | undefined
}

type BulkQuickActionResult = {
  success: boolean
  successCount: number
  failureCount: number
  totalCount: number
  failedImages?: Array<{ pageIndex: number; itemId: string; error: string }>
  error?: string
}

type UseBulkImageQuickActionsResult = {
  applyBulkLightening: () => Promise<BulkQuickActionResult>
  applyBulkRedness: () => Promise<BulkQuickActionResult>
  applyBulkReset: () => Promise<BulkQuickActionResult>
  isProcessing: boolean
}

export const useBulkImageQuickActions = (): UseBulkImageQuickActionsResult => {
  const setIsBulkProcessing = useSetRecoilState(isBulkProcessingAtom)
  const setBulkProcessingProgress = useSetRecoilState(
    bulkProcessingProgressAtom,
  )
  const setUnsavedChanges = useSetRecoilState(unsavedChangesAtom)
  const { applyImageEdit } = useProgrammaticImageEdit()

  const isProcessing = useRecoilValue(isBulkProcessingAtom)

  // Recoil callback to update image state in the editorPagesAtom
  // This updates the source of truth directly so changes persist across page navigation
  const updateImageInPages = useRecoilCallback(
    ({ snapshot, set }) =>
      async (
        pageIndex: number,
        itemId: string,
        editedUrl: string,
        imageEdits: ImageItem['image']['edits'],
        cutout: ProcessedCutout,
      ) => {
        // Get current pages from the atom
        const currentPages = await snapshot.getPromise(editorPagesAtom)

        // Clone and update the specific page with the edited image
        const updatedPages = currentPages.map((page, currentPageIndex) => {
          // Only update the page that contains this image
          const isTargetPage = currentPageIndex === pageIndex
          if (!isTargetPage) {
            return page
          }

          // Update the specific item within the page
          return {
            ...page,
            product: {
              ...page.product,
              items: page.product.items.map((item) => {
                // Only update the target item
                const isTargetItem = item.id === itemId
                if (!isTargetItem) {
                  return item
                }

                // Type assertion: We know this is an ImageItem because we collected it as one
                const imageItem = item as ImageItem

                // Update the image with new edits and editedUrl
                return {
                  ...imageItem,
                  image: {
                    ...imageItem.image,
                    editedUrl,
                    edits: imageEdits,
                    ...cutout,
                  },
                }
              }),
            },
          }
        })

        // Update the entire pages atom with the modified structure
        set(editorPagesAtom, updatedPages)
      },
    [],
  )

  // Sync successfully processed images from editorPagesAtom to itemAtomFamily
  // This ensures UI components reading from itemAtomFamily see the latest changes
  const syncBulkEditsToItemAtomFamily = useRecoilCallback(
    ({ set }) =>
      async (
        processedItems: Array<{
          itemId: string
          itemType: string
          editedUrl: string
          imageEdits: ImageItem['image']['edits']
          cutout: ProcessedCutout
        }>,
      ) => {
        // Update each successfully processed item in itemAtomFamily
        for (const {
          itemId,
          itemType,
          editedUrl,
          imageEdits,
          cutout,
        } of processedItems) {
          // Use itemSelector setters to update nested paths
          // This matches the pattern used in syncImageEditToEditorPages
          set(
            itemSelector({
              id: itemId,
              atomFamily: itemType,
              path: 'image.editedUrl',
            }),
            editedUrl,
          )
          set(
            itemSelector({
              id: itemId,
              atomFamily: itemType,
              path: 'image.edits',
            }),
            imageEdits,
          )
          set(
            itemSelector({
              id: itemId,
              atomFamily: itemType,
              path: 'image.aiVariantId',
            }),
            cutout.aiVariantId,
          )
          set(
            itemSelector({
              id: itemId,
              atomFamily: itemType,
              path: 'image.aiVariantUrl',
            }),
            cutout.aiVariantUrl,
          )
        }
      },
    [],
  )

  /**
   * Collects all processable image items from all pages.
   * Filters out design assets and images that can't be resolved to URLs.
   * Reads the latest state from atoms via snapshot to avoid stale values.
   */
  const collectAllImageItems = useRecoilCallback(
    ({ snapshot }) =>
      async () => {
        // Read the latest state from atoms (not stale hook values)
        const editorPages = await snapshot.getPromise(editorPagesAtom)
        const _case = await snapshot.getPromise(caseAtom)

        const noPages = !editorPages || !Array.isArray(editorPages)
        if (noPages) return []

        // TODO: fix when we update caseAtom to typescript
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const filledImages = (_case as any)?.filledImages

        const imageItems: Array<{
          imageId: string
          imageUrl: string
          pageIndex: number
          item: ImageItem
          page: Content
        }> = []

        editorPages.forEach((page, pageIndex) => {
          const items = page?.product?.items || []

          items.forEach((item) => {
            const isImage = item.type === 'image'

            const itemSubtype = isImage ? item.properties?.subtype : undefined
            const isExcludedSubtype = itemSubtype
              ? (NON_EDITABLE_IMAGE_SUBTYPES as readonly string[]).includes(
                  itemSubtype,
                )
              : false

            // Only process actual photo images (not shapes, QR codes, watermarks, etc.)
            const isProcessableImage = isImage && !isExcludedSubtype

            if (isProcessableImage) {
              // Type narrow to ImageItem since we confirmed item.type === 'image'
              const imageItem = item as ImageItem
              const imageId = `${imageItem.type}-${imageItem.id}`

              // Determine the ORIGINAL image URL (handles placeholders → case images)
              // IMPORTANT: Pass editedUrl: undefined to get the original source, not the edited version
              // This matches the pattern used in useImageQuickActions.determineOriginalImageSource
              // We need the original source for processing, not the edited URL (which would cause cumulative edits)
              const imageUrl = determineImageUrl({
                url: imageItem.image?.url,
                editedUrl: undefined, // Don't use editedUrl - we need the original source for processing
                subtype: imageItem.properties?.subtype,
                caseImage: imageItem.properties?.caseImage,
                filledImages,
              })

              // Only include if we have a valid resolved URL and it's not a placeholder
              const hasValidImageUrl = imageUrl && imageUrl.trim() !== ''
              const isPlaceholder = hasValidImageUrl
                ? isImagePlaceholder(imageUrl)
                : false
              const shouldIncludeImage = hasValidImageUrl && !isPlaceholder
              if (shouldIncludeImage) {
                imageItems.push({
                  imageId,
                  imageUrl,
                  pageIndex,
                  item: imageItem,
                  page,
                })
              }
            }
          })
        })

        return imageItems
      },
    [],
  )

  /**
   * Applies a bulk quick action to all processable images.
   * Uses partial success pattern - continues processing even if some images fail.
   */
  const applyBulkQuickAction = useCallback(
    async (actionType: BulkQuickActionType): Promise<BulkQuickActionResult> => {
      const allImageItems = await collectAllImageItems()

      const noImagesToProcess = allImageItems.length === 0
      if (noImagesToProcess) {
        return {
          success: false,
          successCount: 0,
          failureCount: 0,
          totalCount: 0,
          error: 'No images found to process',
        }
      }

      // Set processing state
      setIsBulkProcessing(true)
      setBulkProcessingProgress({
        processedCount: 0,
        totalCount: allImageItems.length,
        currentAction: actionType,
      })

      // Track results for partial success handling
      let successCount = 0
      const failedImages: Array<{
        pageIndex: number
        itemId: string
        error: string
      }> = []
      // Track successfully processed items to sync to itemAtomFamily after bulk operation
      const successfullyProcessedItems: Array<{
        itemId: string
        itemType: string
        editedUrl: string
        imageEdits: ImageItem['image']['edits']
        cutout: ProcessedCutout
      }> = []

      try {
        // Process each image sequentially
        for (const [
          index,
          { imageUrl, item, pageIndex },
        ] of allImageItems.entries()) {
          try {
            // Use the resolved imageUrl (not item.image.url) to avoid processing placeholders
            // The imageUrl has already been resolved via determineImageUrl during collection
            const originalImageUrl = item.image?.url || ''
            const isPlaceholder = isImagePlaceholder(originalImageUrl)

            // If original is a placeholder, use the resolved imageUrl instead
            const photoUrl = isPlaceholder
              ? imageUrl
              : item.image?.url || imageUrl

            // An image that shows an accepted AI cutout is rendered from the
            // whole cutout, read at the photo's size, so it keeps the removed
            // background and its "Edited with AI" tag.
            const { aiVariantId, aiVariantUrl } = item.image ?? {}
            const cutout: ProcessedCutout =
              aiVariantId && aiVariantUrl
                ? { aiVariantId, aiVariantUrl }
                : { aiVariantId: undefined, aiVariantUrl: undefined }

            // Get existing edits for this image
            const sanitizedExistingImageEdits =
              removeProgrammaticColorAdjustmentsFromImageEdits(
                item.image?.edits,
              )

            // Determine the edit parameters based on action type
            let editParams: {
              gamma?: number
              contrast?: number
              saturation?: number
              reset?: boolean
            }
            if (actionType === 'lightening') {
              // Lightening preset: Gamma 10, Contrast 30
              // Desired gamma percentage from quick action spec (+10%)
              const desiredGammaPercentage = 10
              // Translate UI percentage into Pintura-compatible gamma value
              const convertedGammaValue = convertGammaPercentageToInternalValue(
                desiredGammaPercentage,
              )

              editParams = {
                gamma: convertedGammaValue,
                contrast: 0.15,
              }
            } else if (actionType === 'redness') {
              // Redness preset: Gamma 10, Contrast 30, Saturation -15
              // Desired gamma percentage from quick action spec (+10%)
              const desiredGammaPercentage = 10
              // Translate UI percentage into Pintura-compatible gamma value
              const convertedGammaValue = convertGammaPercentageToInternalValue(
                desiredGammaPercentage,
              )
              // Desired saturation percentage from quick action spec (-15%)
              const desiredSaturationPercentage = -15
              // Translate UI percentage into Pintura-compatible saturation offset
              const convertedSaturationOffset =
                convertSaturationPercentageToOffset(desiredSaturationPercentage)

              editParams = {
                gamma: convertedGammaValue,
                contrast: 0.15,
                saturation: convertedSaturationOffset,
              }
            } else {
              // Reset all adjustments
              editParams = {
                reset: true,
              }
            }

            // Apply the edit using programmatic image edit
            const result = await applyImageEdit({
              ...(cutout.aiVariantUrl
                ? { imageUrl: cutout.aiVariantUrl, cutoutOfPhotoUrl: photoUrl }
                : { imageUrl: photoUrl }),
              existingEdits: sanitizedExistingImageEdits,
              edits: editParams,
            })

            if (!result.success) {
              failedImages.push({
                pageIndex,
                itemId: item.id,
                error: result.error,
              })
            } else {
              await updateImageInPages(
                pageIndex,
                item.id,
                result.editedUrl,
                result.imageState,
                cutout,
              )

              successfullyProcessedItems.push({
                itemId: item.id,
                itemType: item.type,
                editedUrl: result.editedUrl,
                imageEdits: result.imageState,
                cutout,
              })

              successCount++
            }
          } catch (imageError) {
            failedImages.push({
              pageIndex,
              itemId: item.id,
              error:
                imageError instanceof Error
                  ? imageError.message
                  : 'Unknown error processing image',
            })
          }

          // Update progress (counts both successes and failures)
          setBulkProcessingProgress({
            processedCount: index + 1,
            totalCount: allImageItems.length,
            currentAction: actionType,
          })
        }

        // Sync successfully processed items to itemAtomFamily
        // This ensures UI components reading from itemAtomFamily see the latest changes
        // and prevents display issues when mixing bulk and single edits
        const hasSuccessfullyProcessedItems =
          successfullyProcessedItems.length > 0
        if (hasSuccessfullyProcessedItems) {
          await syncBulkEditsToItemAtomFamily(successfullyProcessedItems)
        }

        // Mark changes as unsaved if any images were successfully processed
        const anyImagesSucceeded = successCount > 0
        if (anyImagesSucceeded) {
          setUnsavedChanges(true)
        }

        // Determine overall success
        // Success = at least one image processed successfully
        const overallSuccess = successCount > 0

        return {
          success: overallSuccess,
          successCount,
          failureCount: failedImages.length,
          totalCount: allImageItems.length,
          failedImages: failedImages.length > 0 ? failedImages : undefined,
        }
      } catch (error) {
        // Catch catastrophic errors
        return {
          success: false,
          successCount,
          failureCount: failedImages.length,
          totalCount: allImageItems.length,
          failedImages: failedImages.length > 0 ? failedImages : undefined,
          error: error instanceof Error ? error.message : 'Unknown error',
        }
      } finally {
        // Always reset processing state
        setIsBulkProcessing(false)
        setBulkProcessingProgress({
          processedCount: 0,
          totalCount: 0,
          currentAction: '',
        })
      }
    },
    [
      collectAllImageItems,
      setIsBulkProcessing,
      setBulkProcessingProgress,
      setUnsavedChanges,
      applyImageEdit,
      updateImageInPages,
      syncBulkEditsToItemAtomFamily,
    ],
  )

  const applyBulkLightening = useCallback(async () => {
    return await applyBulkQuickAction('lightening')
  }, [applyBulkQuickAction])

  const applyBulkRedness = useCallback(async () => {
    return await applyBulkQuickAction('redness')
  }, [applyBulkQuickAction])

  const applyBulkReset = useCallback(async () => {
    return await applyBulkQuickAction('reset')
  }, [applyBulkQuickAction])

  return {
    applyBulkLightening,
    applyBulkRedness,
    applyBulkReset,
    isProcessing,
  }
}
